/**
 * Phase 8A — TS model for MIS templates, mirroring PG shape.
 */

export type MisCadenceMonths = 1 | 2 | 3 | 6 | 12;
export const ALLOWED_MIS_CADENCE: MisCadenceMonths[] = [1, 2, 3, 6, 12];

export const CADENCE_LABEL: Record<MisCadenceMonths, string> = {
  1: "Monthly",
  2: "Every 2 months",
  3: "Quarterly",
  6: "Semi-annual",
  12: "Annual",
};

export type MisActualsSource = "projected" | "actual";
export const ACTUALS_SOURCE_LABEL: Record<MisActualsSource, string> = {
  projected: "Projected (approved budgets)",
  actual: "Actual (invoices / payouts)",
};

export interface Mis {
  id: string;
  name: string;
  description: string | null;
  cadence_months: MisCadenceMonths;
  start_fy: string;
  actuals_source: MisActualsSource;
  active: boolean;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface MisCreateInput {
  name: string;
  description?: string;
  cadence_months: MisCadenceMonths;
  start_fy: string;
  actuals_source: MisActualsSource;
}

export interface MisUpdateInput extends Partial<MisCreateInput> {
  active?: boolean;
}

// -------- Phase 8B: sections + sub-categories --------

export type MisSectionType = "revenue" | "expense" | "formula";

export const SECTION_TYPE_LABEL: Record<MisSectionType, string> = {
  revenue: "Revenue",
  expense: "Expense",
  formula: "Formula",
};

export const SECTION_TYPE_HINT: Record<MisSectionType, string> = {
  revenue:
    "Sub-categories are abstract (Charge-points, EMS, …). Numbers entered manually per BU × period.",
  expense:
    "Sub-categories are tagged Cost Centers. Numbers pulled from Mongo actuals.",
  formula:
    "Computed section — e.g. CM1 = a − b. Cannot have sub-categories.",
};

export interface MisSection {
  id: string;
  mis_id: string;
  section_type: MisSectionType;
  title: string;
  display_order: number;
  formula_expression: string | null;
  show_percentage: boolean;
  percentage_of_section_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface MisSubCategory {
  id: string;
  section_id: string;
  label: string;
  cost_center_ids: string[]; // empty for revenue, >=1 for expense
  display_order: number;
  created_at: string;
  updated_at: string;
}

/** Convert display_order (0-based) → letter (a, b, c, …). */
export const orderToLetter = (n: number): string => {
  if (n < 0) return "?";
  // Support beyond 26 via aa, ab, … just in case.
  if (n < 26) return String.fromCharCode(97 + n);
  const first = Math.floor(n / 26) - 1;
  const second = n % 26;
  return String.fromCharCode(97 + first) + String.fromCharCode(97 + second);
};

/** Render a formula expression, replacing {uuid} tokens with letters. */
export const renderFormula = (
  expression: string | null,
  sectionOrderById: Record<string, number>,
): string => {
  if (!expression) return "";
  return expression.replace(/\{([0-9a-fA-F-]{36})\}/g, (_, id) => {
    const ord = sectionOrderById[id];
    if (ord === undefined) return "?";
    return orderToLetter(ord);
  });
};
